<template>
  <div class="mt-4 flex flex-col gap-6">
    <div v-if="conversationNotes.length > 0" class="break-inside-avoid">
      <h3 class="text-title-medium">{{ $t('comment.conversation-note.title') }}</h3>
      <ul class="mt-2 grid grid-cols-2 gap-4">
        <li v-for="comment in conversationNotes" :key="comment.id">
          <div
            class="rounded-small border-outline-variant bg-tertiary-container flex break-inside-avoid flex-col border p-2"
          >
            <div class="mb-2 flex flex-row items-center">
              <div
                class="bg-tertiary flex h-8 w-8 shrink-0 flex-row items-center justify-center rounded-full"
              >
                <BaseIcon class="text-on-tertiary" size="20">group</BaseIcon>
              </div>
              <div class="ml-2 flex flex-col">
                <span class="text-label-large font-medium">
                  {{ $t('comment.conversation-note.label') }}
                </span>
                <span class="text-body-medium text-on-surface-variant -mt-1">
                  {{
                    useDisplayDate(comment.createdAt, {
                      dayName: 'none',
                      monthName: 'number',
                      year: 'short',
                      displayYear: 'always',
                    })
                  }}
                </span>
              </div>
            </div>

            <WysiwygTextFieldReadOnly :value="comment.text" />
          </div>
        </li>
      </ul>
    </div>

    <div v-if="individualComments.length > 0" class="break-inside-avoid">
      <h3 class="text-title-medium">
        {{ conversationNotes.length > 0 ? $t('comment.title.individual') : 'Kommentare' }}
      </h3>
      <ul class="mt-2 grid grid-cols-2 gap-4">
        <li v-for="comment in individualComments" :key="comment.id">
          <div
            class="rounded-small border-outline-variant flex break-inside-avoid flex-col border p-2"
          >
            <div class="mb-2 flex flex-row items-center">
              <BaseMemberAvatar
                size="32"
                :member="getMemberAvatar(comment.author.id) ?? comment.author"
              />
              <div class="ml-2 flex flex-col">
                <span class="text-label-large font-medium">{{
                  memberFullName(comment.author)
                }}</span>
                <span class="text-body-medium text-on-surface-variant -mt-1">
                  {{
                    useDisplayDate(comment.createdAt, {
                      dayName: 'none',
                      monthName: 'number',
                      year: 'short',
                      displayYear: 'always',
                    })
                  }}
                </span>
              </div>
            </div>

            <WysiwygTextFieldReadOnly :value="comment.text" />
          </div>
        </li>
      </ul>
    </div>
  </div>
</template>
<script lang="ts" setup>
  import { computed } from 'vue'
  import BaseIcon from '@/core/components/base/icon/BaseIcon.vue'
  import BaseMemberAvatar from '@/core/components/base/avatar/BaseMemberAvatar.vue'
  import WysiwygTextFieldReadOnly from '@/core/components/base/wysiwyg/WysiwygTextFieldReadOnly.vue'
  import type { Comment } from '@/core/composables/use-comments'
  import { useDisplayDate } from '@/core/composables/use-display-date'
  import type { MemberOrUser } from '@/core/models/member/member-or-user'
  import { memberFullName } from '@/core/models/member/member-name'

  interface Props {
    comments: ReadonlyArray<Comment>
    members: ReadonlyArray<MemberOrUser>
  }
  const props = defineProps<Props>()

  const conversationNotes = computed(() =>
    props.comments.filter((comment) => comment.isConversationNote),
  )

  const individualComments = computed(() =>
    props.comments.filter((comment) => !comment.isConversationNote),
  )

  const getMemberAvatar = (memberId: string) => {
    return props.members.find((member) => member.id === memberId)
  }
</script>
